export const PLAYER_MATCH_CONFIG = {
  fuzzyThreshold: 0.85,
  ambiguityMargin: 0.08,
};

export const normalizePlayerNameForMatching = (name) => (
  String(name || '')
    .normalize('NFKC')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
);

const normalizeOcrConfusablesForSimilarity = (name) => (
  normalizePlayerNameForMatching(name)
    .replace(/[1!|]/g, 'i')
    .replace(/0/g, 'o')
    .replace(/5/g, 's')
);

const levenshteinDistance = (a, b) => {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  const previous = Array.from({ length: b.length + 1 }, (_, index) => index);
  const current = Array(b.length + 1).fill(0);

  for (let i = 1; i <= a.length; i += 1) {
    current[0] = i;
    for (let j = 1; j <= b.length; j += 1) {
      const substitutionCost = a[i - 1] === b[j - 1] ? 0 : 1;
      current[j] = Math.min(
        previous[j] + 1,
        current[j - 1] + 1,
        previous[j - 1] + substitutionCost
      );
    }

    for (let j = 0; j <= b.length; j += 1) {
      previous[j] = current[j];
    }
  }

  return previous[b.length];
};

export const getNameSimilarity = (leftName, rightName) => {
  const left = normalizePlayerNameForMatching(leftName);
  const right = normalizePlayerNameForMatching(rightName);

  if (!left || !right) return 0;
  if (left === right) return 1;

  const maxLength = Math.max(left.length, right.length);
  const baseSimilarity = maxLength > 0
    ? (maxLength - levenshteinDistance(left, right)) / maxLength
    : 0;
  const ocrLeft = normalizeOcrConfusablesForSimilarity(left);
  const ocrRight = normalizeOcrConfusablesForSimilarity(right);
  const ocrMaxLength = Math.max(ocrLeft.length, ocrRight.length);
  const ocrSimilarity = ocrMaxLength > 0
    ? (ocrMaxLength - levenshteinDistance(ocrLeft, ocrRight)) / ocrMaxLength
    : 0;

  return Math.max(baseSimilarity, ocrSimilarity);
};

const createSourceSummary = (record, extra = {}) => ({
  sourceRowId: record.sourceRowId,
  sourceRowIndex: record.sourceRowIndex,
  sourceScreenshotIndex: record.sourceScreenshotIndex,
  sourceScreenshotName: record.sourceScreenshotName,
  detectedName: record.detectedName,
  ...extra,
});

const makeCandidate = (player, sourceRecord, index) => {
  const playerKey = normalizePlayerNameForMatching(player.detectedName);
  const sourceKey = normalizePlayerNameForMatching(sourceRecord.detectedName);

  if (!playerKey || !sourceKey) {
    return {
      playerIndex: index,
      score: 0,
      type: 'none',
    };
  }

  if (playerKey === sourceKey) {
    return {
      playerIndex: index,
      score: 1,
      type: 'exact',
    };
  }

  return {
    playerIndex: index,
    score: getNameSimilarity(playerKey, sourceKey),
    type: 'fuzzy',
  };
};

const findBestMatch = ({
  players,
  sourceRecord,
  consumedPlayerIndexes = new Set(),
  config = PLAYER_MATCH_CONFIG,
}) => {
  const candidates = players
    .map((player, index) => makeCandidate(player, sourceRecord, index))
    .filter((candidate) => !consumedPlayerIndexes.has(candidate.playerIndex));

  const exactMatches = candidates.filter((candidate) => candidate.type === 'exact');
  if (exactMatches.length === 1) {
    return {
      status: 'matched',
      playerIndex: exactMatches[0].playerIndex,
      type: 'exact',
      score: 1,
      candidates: exactMatches,
    };
  }

  if (exactMatches.length > 1) {
    return {
      status: 'ambiguous',
      type: 'exact',
      score: 1,
      candidates: exactMatches,
    };
  }

  const fuzzyCandidates = candidates
    .filter((candidate) => candidate.type === 'fuzzy' && candidate.score >= config.fuzzyThreshold)
    .sort((a, b) => b.score - a.score);

  if (!fuzzyCandidates.length) {
    return {
      status: 'unmatched',
      candidates: candidates
        .filter((candidate) => candidate.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 3),
    };
  }

  const [best, secondBest] = fuzzyCandidates;
  if (secondBest && best.score - secondBest.score < config.ambiguityMargin) {
    return {
      status: 'ambiguous',
      type: 'fuzzy',
      score: best.score,
      candidates: fuzzyCandidates.slice(0, 3),
    };
  }

  return {
    status: 'matched',
    playerIndex: best.playerIndex,
    type: 'fuzzy',
    score: best.score,
    candidates: fuzzyCandidates.slice(0, 3),
  };
};

const formatScore = (score) => Math.round(score * 1000) / 1000;

const describeCandidates = (players, candidates = []) => candidates.map((candidate) => ({
  playerIndex: candidate.playerIndex,
  detectedName: players[candidate.playerIndex]?.detectedName || '',
  type: candidate.type,
  score: formatScore(candidate.score),
}));

const createBasePlayers = (matchRecords) => matchRecords.map((matchRecord) => ({
  detectedName: matchRecord.detectedName,

  playerId: null,
  playerName: '',
  operator: '',

  score: matchRecord.score ?? null,
  kills: matchRecord.kills ?? null,
  deaths: matchRecord.deaths ?? null,
  assists: matchRecord.assists ?? null,
  impact: matchRecord.impact ?? null,

  damage: null,
  captureKills: null,
  operatorValue: null,

  _sources: {
    matchDetails: createSourceSummary(matchRecord, {
      score: matchRecord.score ?? null,
      kills: matchRecord.kills ?? null,
      deaths: matchRecord.deaths ?? null,
      assists: matchRecord.assists ?? null,
      impact: matchRecord.impact ?? null,
    }),
    impactDetails: null,
    propDetails: [],
  },
  _matchConfidence: {
    impact: null,
    prop: null,
  },
  _warnings: [...(matchRecord._warnings || [])],
}));

const attachImpactRecords = ({ players, impactRecords, result, config }) => {
  const consumedImpactIndexes = new Set();
  const consumedPlayerIndexes = new Set();

  impactRecords.forEach((impactRecord, impactIndex) => {
    const match = findBestMatch({
      players,
      sourceRecord: impactRecord,
      consumedPlayerIndexes,
      config,
    });

    if (match.status === 'matched') {
      const player = players[match.playerIndex];
      player.damage = impactRecord.damage ?? null;
      player.captureKills = impactRecord.captureKills ?? null;
      player._sources.impactDetails = createSourceSummary(impactRecord, {
        damage: impactRecord.damage ?? null,
        captureKills: impactRecord.captureKills ?? null,
        matchType: match.type,
        matchScore: formatScore(match.score),
      });
      player._matchConfidence.impact = {
        type: match.type,
        score: formatScore(match.score),
      };
      consumedImpactIndexes.add(impactIndex);
      consumedPlayerIndexes.add(match.playerIndex);
      result.stats[match.type === 'exact' ? 'exactMatches' : 'fuzzyMatches'] += 1;
      return;
    }

    const debugRecord = {
      ...createSourceSummary(impactRecord, {
        damage: impactRecord.damage ?? null,
        captureKills: impactRecord.captureKills ?? null,
      }),
      candidates: describeCandidates(players, match.candidates),
    };

    if (match.status === 'ambiguous') {
      result.ambiguous.impact.push(debugRecord);
      result.stats.ambiguousMatches += 1;
    } else {
      result.unmatched.impact.push(debugRecord);
      result.stats.unmatchedRecords += 1;
    }
  });

  return consumedImpactIndexes;
};

const flattenPropResults = (propResults) => propResults.flatMap((propResult, resultIndex) => (
  (propResult?.records || []).map((record) => ({
    ...record,
    sourceScreenshotIndex: record.sourceScreenshotIndex ?? propResult.sourceScreenshotIndex ?? resultIndex,
    sourceScreenshotName: record.sourceScreenshotName || propResult.sourceScreenshotName || `Prop Details ${resultIndex + 1}`,
  }))
));

const addPropToPlayer = ({ player, propRecord, match, result }) => {
  const propSource = createSourceSummary(propRecord, {
    operatorValue: propRecord.operatorValue ?? null,
    matchType: match.type,
    matchScore: formatScore(match.score),
  });
  player._sources.propDetails.push(propSource);

  if (!player._matchConfidence.prop || match.score > player._matchConfidence.prop.score) {
    player._matchConfidence.prop = {
      type: match.type,
      score: formatScore(match.score),
    };
  }

  if (propRecord.operatorValue == null) {
    if (!player._warnings.includes('Prop Operator Value is unresolved.')) {
      player._warnings.push('Prop Operator Value is unresolved.');
    }
    return;
  }

  if (player.operatorValue == null && !player._propValueConflict) {
    player.operatorValue = propRecord.operatorValue;
    return;
  }

  if (player.operatorValue === propRecord.operatorValue) {
    return;
  }

  const values = new Set([
    ...(player._propValueCandidates || []),
    player.operatorValue,
    propRecord.operatorValue,
  ].filter((value) => value != null));
  const candidates = Array.from(values);
  player.operatorValue = null;
  player._propValueConflict = true;
  player._propValueCandidates = candidates;
  const warning = `Conflicting Prop Operator Values detected for ${player.detectedName || 'player'}: ${candidates.join(' vs ')}.`;
  if (!player._warnings.includes(warning)) {
    player._warnings.push(warning);
  }
  result.warnings.push(warning);
};

const attachPropRecords = ({ players, propRecords, result, config }) => {
  const consumedPropIndexes = new Set();

  propRecords.forEach((propRecord, propIndex) => {
    const match = findBestMatch({
      players,
      sourceRecord: propRecord,
      consumedPlayerIndexes: new Set(),
      config,
    });

    if (match.status === 'matched') {
      addPropToPlayer({
        player: players[match.playerIndex],
        propRecord,
        match,
        result,
      });
      consumedPropIndexes.add(propIndex);
      result.stats[match.type === 'exact' ? 'exactMatches' : 'fuzzyMatches'] += 1;
      return;
    }

    const debugRecord = {
      ...createSourceSummary(propRecord, {
        operatorValue: propRecord.operatorValue ?? null,
      }),
      candidates: describeCandidates(players, match.candidates),
    };

    if (match.status === 'ambiguous') {
      result.ambiguous.prop.push(debugRecord);
      result.stats.ambiguousMatches += 1;
    } else {
      result.unmatched.prop.push(debugRecord);
      result.stats.unmatchedRecords += 1;
    }
  });

  return consumedPropIndexes;
};

export const mergeControlPlayerRows = ({
  matchDetails,
  impactDetails,
  propDetails = [],
  config = PLAYER_MATCH_CONFIG,
} = {}) => {
  const matchRecords = matchDetails?.records || [];
  const impactRecords = impactDetails?.records || [];
  const propResults = Array.isArray(propDetails) ? propDetails : [propDetails].filter(Boolean);
  const propRecords = flattenPropResults(propResults);
  const players = createBasePlayers(matchRecords);
  const result = {
    mode: 'control',
    players,
    unmatched: {
      impact: [],
      prop: [],
    },
    ambiguous: {
      impact: [],
      prop: [],
    },
    warnings: [],
    stats: {
      matchPlayers: matchRecords.length,
      impactRecords: impactRecords.length,
      propRecords: propRecords.length,
      exactMatches: 0,
      fuzzyMatches: 0,
      ambiguousMatches: 0,
      unmatchedRecords: 0,
    },
  };

  if (!matchRecords.length) {
    result.warnings.push('No Match Details records were available to anchor the Control merge.');
    result.unmatched.impact = impactRecords.map((record) => createSourceSummary(record, {
      damage: record.damage ?? null,
      captureKills: record.captureKills ?? null,
      candidates: [],
    }));
    result.unmatched.prop = propRecords.map((record) => createSourceSummary(record, {
      operatorValue: record.operatorValue ?? null,
      candidates: [],
    }));
    result.stats.unmatchedRecords = impactRecords.length + propRecords.length;
    return result;
  }

  attachImpactRecords({ players, impactRecords, result, config });
  attachPropRecords({ players, propRecords, result, config });

  players.forEach((player) => {
    if (!player._sources.impactDetails) {
      player._warnings.push('No Impact Details row matched this player.');
    }

    if (!player._sources.propDetails.length) {
      player._warnings.push('No Prop Details row matched this player.');
    }
  });

  return result;
};

export default mergeControlPlayerRows;
